import {defineStore} from 'pinia';
import i18n from "@/i18n";
import axios from "axios";
import {notificationStore} from "@/store/notificationStore";
import {sessionStore} from "@/store/sessionStore";
import {userStore} from "@/store/userStore";
import {successMessageStore} from "@/store/successMessageStore";

export const authStore = defineStore('authStore',{
    state: () => ({
        userType: null,
        userInfo: null,
        linkedUserInfo: null,
        userProfiles: [],
        activeProfileInfo:null,

        managerProfileInfo: null,
        userIsActiveManager: false,
        userApplications: [],
        acceptedCookie: true,
        approveOrgList: [],
        notApproveOrgList: [],
        needVerifyUserModalState: false,

        // Auth modal
        loginModalState: false,
        loginCancelCallback: null,

        // Sign up modal
        signUpModalState: false,
        signUpCustomCallback: null,
        signUpErrorCallback: null,

        successfulSignUpModalState: false,

        emailExistModalState: false,

        // Reset password modal
        resetPasswordModalState: false,
        resetPasswordModalCloseCallback: null,

        // Change email modal
        changeEmailModalState: false,

        // Create org
        createOrgState: false,
        registerPartyInfoState: false,

        // Leave page confirm modal
        leavePageModal: false,
        hasUnsavedChanges: true,
        unsavedChangesCallback: null,

        //Manager
        managerViewTotals: null,
        managerApplicationsCount: 0
    }),
    getters: {
        userActiveProfile() {
            return this.userProfiles.length > 0
                ? this.userProfiles.filter((profile)=>{return profile.active})[0]
                : {type: '', profileId: ''};
        },
        isManagerProfile() {
            return this.userActiveProfile.type === 'MANAGER';
        },
        hasHeaderMessage() {
            return !userStore().userHasApprovedEmail || !this.acceptedCookie;
        },
        userLoggedIn() {
            return this.userInfo !== null;
        },
        userIsManager() {
            return this.managerProfileInfo !== null;
        },
        showCookiePolicyMessage() {
            return !this.acceptedCookie && !this.userLoggedIn;
        },
        userTimeZoneId() {
            if (this.userInfo !== null) {
                return this.userInfo.tmzId ? this.userInfo.tmzId : 'Europe/Stockholm';
            }
            return 'Europe/Stockholm';
        },
        approveOrgCount() {
            return this.approveOrgList.length;
        },
        permissionUserRole() {
            return this.userActiveProfile.type;
        }
    },
    actions: {
        authGetUserData() {
            return sessionStore().getSessionProfilesAction().then((res) => {
                this.userType = res.data.type;
                this.userInfo = res.data.user;
                this.linkedUserInfo = res.data.linkedUser;
                this.userProfiles = res.data.profiles ? res.data.profiles : [];
                userStore().getUserEmails(this.userProfiles);
            }).catch(error => console.log(error));
        },
        getUserAccounts(id) {
            return axios.get(`/api/user/accounts?accountIds=${id}`);
        },
        isSameAccount(accountId) {
            if (this.userInfo !== null && this.userInfo.id === accountId) {
                return true;
            }
            return this.linkedUserInfo !== null && this.linkedUserInfo.id === accountId;

        },

        //Profiles
        getMemberProfile(profileId) {
            return axios.get(`/api/member/profiles/${profileId}`)
                .then((resp)=> {
                    this.activeProfileInfo = resp.data;
                });
        },
        getManagerProfile() {
            return axios.get('/api/manager/profile')
                .then((resp)=> {
                    this.managerProfileInfo = resp.data;
                    this.activeProfileInfo = resp.data;
                    if (this.managerProfileInfo) {
                        this.userIsActiveManager = this.managerProfileInfo.status === 'ACTIVE';
                    }
                }).catch(error => console.log(error));
        },

        // Auth modal
        loginAction(formData) {
            const config = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }};
            return axios.post('/api/login', formData, config);
        },
        logoutAction() {
            return axios.post('/api/logout')
                .then(() => {
                    this.userInfo = null;
                    this.managerProfileInfo = null;
                    notificationStore().cleanNotificationStore();
                    userStore().clearUserStore();
                })
                .catch(error => console.log(error));
        },
        getApprovedOrganizations() {
            axios.get('/api/user/desktop/organizations/approved')
                .then((res) => {
                    this.approveOrgList = res.data;
                })
                .catch(error => console.log(error));
        },
        getCurrentOrgApplications() {
            axios.get('/currentorgapplications?page.page=1&page.size=20')
                .then((res) => {
                    this.notApproveOrgList = res.data.content;
                })
        },
        setActiveProfileAction(profile) {
            return axios.put(`/api/session/profiles?${new URLSearchParams(profile).toString()}`);
        },

        setCookieAcceptedAction() {
            localStorage.setItem('coockieTerms:accepted', true);
        },
        checkCookieAcceptedAction() {
            this.acceptedCookie = localStorage.getItem('coockieTerms:accepted', true) === 'true';
        },
        toggleLoginModalState(cancelCallback) {
            this.loginModalState = !this.loginModalState;

            if (this.loginModalState && cancelCallback) {
                this.loginCancelCallback = cancelCallback
            }

            if (!this.loginModalState && this.loginCancelCallback) {
                this.loginCancelCallback();
                this.loginCancelCallback = null;
            }
        },

        // Sign up modal
        openSignUpModal(signUpCallback) {
            this.signUpModalState = true;

            if (signUpCallback) {
                this.signUpCustomCallback = signUpCallback;
            }
        },
        closeSignUpModal() {
            this.signUpModalState = false;
            localStorage.removeItem('notAuthTakeOverProcess');
        },
        setSignUpErrorCallback(errorCallback) {
            if (errorCallback) {
                this.signUpErrorCallback = errorCallback;
            }
        },
        setSignUpCustomCallback(signUpCustomCallback) {
            if (signUpCustomCallback) {
                this.signUpCustomCallback = signUpCustomCallback;
            }
        },
        signUpRequest(formData) {
            return axios.post('/api/registration/join', formData)
                .then(() => {
                    if (this.signUpCustomCallback) {
                        this.signUpCustomCallback();
                        this.signUpCustomCallback = null;
                    } else {
                        localStorage.setItem("signUpCallback", "true");
                        window.parent.window.location.href='/';
                    }
                }).catch((error) => {
                    if (this.signUpErrorCallback) {
                        this.signUpErrorCallback(error);
                        this.signUpErrorCallback = null;
                    }
                });
        },
        toggleEmailExistModalState() {
            this.emailExistModalState = !this.emailExistModalState;
        },
        initSignUpView(signUpCallback) {
            if (signUpCallback) {
                this.signUpCustomCallback = signUpCallback;
            }
        },
        openSuccessfulSignUpModal() {
            this.successfulSignUpModalState = true;
        },
        closeSuccessfulSignUpModal() {
            this.successfulSignUpModalState = false;
            localStorage.setItem("signUpCallback", 'false');
        },

        // User applications
        getUserApplications() {
            return axios.get('/api/user/desktop/applications?type=BECOME_MANAGER').then((resp) => {
                this.userApplications = resp.data.content;
            });
        },

        //Manager
        getManagerViewTotals() {
            axios.get('/api/user/desktop/totals').then((resp)=> {
                this.managerViewTotals = resp.data;
            })
        },
        getManagerActiveApplicationsCount() {
            axios.get('/api/manager/application/counts').then((resp)=> {
                this.managerApplicationsCount = resp.data.total.ACTIVE || 0;
            })
        },
        sendApplicationAction(data) {
            return axios.post('/api/manager/application', data);
        },


        // Reset password modal
        openResetPasswordModal(closeCallback) {
            this.resetPasswordModalState = true;

            if (closeCallback) {
                this.resetPasswordModalCloseCallback = closeCallback;
            }
        },
        closeResetPasswordModal() {
            this.resetPasswordModalState = false;
            if (this.resetPasswordModalCloseCallback) {
                this.resetPasswordModalCloseCallback();
                this.resetPasswordModalCloseCallback = null;
            }
        },
        sendPasswordAction(email) {
            return axios.post(`/api/auth/sendpassword?email=${email}`)
                .then(() => {
                    this.resetPasswordModalCloseCallback = null;
                    this.closeResetPasswordModal();
                });
        },

        // Change email modal
        toggleChangeEmailModalState() {
            this.changeEmailModalState = !this.changeEmailModalState;
        },
        resendRegistrationEmail() {
            return axios.post('/api/user/emails/confirm')
                .catch(error => console.log(error));
        },
        addEmailAction(params) {
            return axios.post(`/api/user/emails`, params);
        },
        updateEmailAction(params) {
            let updateParams = {
                email: params.email,
                setPrimary: true
            };
            return axios.put(`/api/user/emails`, updateParams);
        },
        changeEmail(params) {
            this.addEmailAction(params).then(() => {
                this.updateEmailAction(params).then(() => {
                    this.toggleChangeEmailModalState();
                    successMessageStore().showSuccessMessage(i18n.global.t('header.message.noApprovedUser.emailHasBeenChanged'), 500);
                    userStore().getUserEmails(this.userProfiles).then(()=> {
                        userStore().removePrevUserEmails();
                    });
                });
            }).catch(error => console.log(error));
        },
        checkUserIsApproved() {
            let state = true;

            if (!userStore().userHasApprovedEmail) {
                this.toggleNeedVerifyUserModal();
                state = false;
            }

            return state;
        },
        toggleNeedVerifyUserModal() {
            return this.needVerifyUserModalState = !this.needVerifyUserModalState;
        },

        //Create org
        toggleCreateOrgModal() {
            this.createOrgState = !this.createOrgState;
        },
        toggleRegisterPartyInfoModal() {
            this.registerPartyInfoState = !this.registerPartyInfoState;
        },

        // Leave page confirm modal
        setUnsavedChanges(state, callback) {
            this.hasUnsavedChanges = state;

            if (callback) {
                this.unsavedChangesCallback = callback;
            }
        },
        toggleLeavePageModal() {
            this.leavePageModal = !this.leavePageModal;
        },
    }
});